import { useCallback, useEffect, useRef } from 'react';

type ObserverLoaderProps = {
  onIntersect: () => void;
  isLoading: boolean;
  hasMore: boolean;
  rootMargin?: string;
};

export function ObserverLoader({
  onIntersect,
  isLoading,
  hasMore,
  rootMargin = '100px',
}: ObserverLoaderProps) {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const lastTriggerTimeRef = useRef<number>(0);

  const debouncedOnIntersect = useCallback(() => {
    const now = Date.now();
    const timeSinceLastTrigger = now - lastTriggerTimeRef.current;

    if (timeSinceLastTrigger < 500) return;

    lastTriggerTimeRef.current = now;
    onIntersect();
  }, [onIntersect]);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin,
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !isLoading) {
        debouncedOnIntersect();
      }
    }, options);

    if (loadMoreTriggerRef.current) {
      observer.observe(loadMoreTriggerRef.current);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, isLoading, debouncedOnIntersect, rootMargin]);

  return (
    <div
      ref={loadMoreTriggerRef}
      style={{ height: '10px', visibility: 'hidden' }}
      aria-hidden="true"
    />
  );
}
